<template>
  <section v-if="tableData.rows.length" class="curriculum-section">
    <div v-if="tableData.title || defaultTitle" class="curriculum-heading">
      <span class="curriculum-eyebrow">{{ eyebrow }}</span>
      <h2 v-html="formatInline(tableData.title || defaultTitle)"></h2>
      <p
        v-if="tableData.description"
        v-html="formatInline(tableData.description)"
      ></p>
    </div>

    <div
      class="curriculum-table-scroll"
      :class="{
        'section-mode': tableData.hasSections,
        'gomath-mode': tableData.hasGoMath,
        'compact-mode':
          hasInlineList && !tableData.hasSections && !tableData.hasGoMath,
        'image-mode': hasImageHeavyTable,
        'course-mode': tableData.hasCourseGroups,
      }"
    >
      <!-- Go Math：左側圖片＋年級文字，右側 Volume 格狀表格 -->
      <div v-if="tableData.hasGoMath" class="gomath-table-group">
        <table
          v-for="(section, sectionIndex) in tableData.goMathSections"
          :key="`gomath-${section.title}-${sectionIndex}`"
          class="curriculum-table gomath-table"
        >
          <colgroup>
            <col class="gomath-book-col" />
            <col
              v-for="n in section.maxColumns"
              :key="n"
              class="gomath-volume-col"
              :style="{ width: `${75 / section.maxColumns}%` }"
            />
          </colgroup>
          <tbody>
            <tr class="section-title-row">
              <td
                :colspan="section.maxColumns + 1"
                class="section-title-cell"
                v-html="formatInline(section.title)"
              ></td>
            </tr>
            <tr>
              <td class="gomath-book-cell">
                <template
                  v-for="(line, lineIndex) in getCellLines(
                    section.bookCell,
                    true,
                  )"
                  :key="lineIndex"
                >
                  <div v-if="line.type === 'image'" class="cell-image-wrap">
                    <img
                      :src="line.src"
                      :alt="line.alt || line.caption || section.title"
                      class="cell-image"
                      loading="lazy"
                      @click="
                        openImage(
                          line.src,
                          line.alt || line.caption || section.title,
                        )
                      "
                      @error="handleImageError"
                    />
                    <div
                      v-if="line.caption"
                      class="cell-image-caption"
                      v-html="formatInline(line.caption)"
                    ></div>
                  </div>
                  <div
                    v-else
                    class="cell-text"
                    v-html="formatInline(line.text)"
                  ></div>
                </template>
              </td>
              <td :colspan="section.maxColumns" class="gomath-volumes-cell">
                <div
                  class="gomath-volume-grid"
                  :style="{ '--gomath-cols': section.maxColumns }"
                >
                  <div
                    v-for="(volume, volumeIndex) in section.volumes"
                    :key="volumeIndex"
                    class="gomath-volume-card"
                  >
                    <div
                      class="gomath-volume-name"
                      v-html="formatInline(volume.name)"
                    ></div>
                    <div
                      class="gomath-chapter"
                      v-html="formatInline(volume.chapter)"
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Start Right Reader 這類：Book / 書本 / Phonics / HFW 跨 Week 合併 -->
      <table
        v-else-if="isBookWeekTable"
        class="curriculum-table book-week-table"
      >
        <colgroup>
          <col class="book-week-book-col" />
          <col class="book-week-cover-col" />
          <col class="book-week-week-col" />
          <col class="book-week-content-col" />
          <col class="book-week-phonics-col" />
          <col class="book-week-hfw-col" />
        </colgroup>

        <thead>
          <tr>
            <th
              v-for="(header, index) in tableData.headers"
              :key="`book-week-header-${index}`"
              v-html="formatInline(header)"
            ></th>
          </tr>
        </thead>

        <tbody>
          <template
            v-for="(group, groupIndex) in bookWeekGroups"
            :key="`book-week-group-${groupIndex}`"
          >
            <tr
              v-for="(weekRow, weekIndex) in group.weeks"
              :key="`book-week-${groupIndex}-${weekIndex}`"
              :class="{
                'book-week-group-start': weekIndex === 0,
                'kindergarten-small': isKindergartenBookWeekTable && groupIndex <= 1,
                'kindergarten-middle':
                  isKindergartenBookWeekTable && groupIndex >= 2 && groupIndex <= 5,
                'kindergarten-large': isKindergartenBookWeekTable && groupIndex >= 6,
              }"
            >
              <template v-if="weekIndex === 0">
                <td
                  :rowspan="group.weeks.length"
                  class="book-week-book-cell is-first-column"
                >
                  <div class="cell-text" v-html="formatInline(group.book)"></div>
                </td>

                <td
                  :rowspan="group.weeks.length"
                  class="book-week-cover-cell"
                >
                  <template
                    v-for="(line, lineIndex) in getCellLines(group.cover, true)"
                    :key="lineIndex"
                  >
                    <div v-if="line.type === 'image'" class="cell-image-wrap">
                      <img
                        :src="line.src"
                        :alt="line.alt || line.caption || group.book"
                        class="cell-image"
                        loading="lazy"
                        @click="
                          openImage(
                            line.src,
                            line.alt || line.caption || group.book,
                          )
                        "
                        @error="handleImageError"
                      />
                      <div
                        v-if="line.caption"
                        class="cell-image-caption"
                        v-html="formatInline(line.caption)"
                      ></div>
                    </div>
                    <div
                      v-else
                      class="cell-text"
                      v-html="formatInline(line.text)"
                    ></div>
                  </template>
                </td>
              </template>

              <td class="book-week-week-cell">
                <div class="cell-text" v-html="formatInline(weekRow.week)"></div>
              </td>

              <td class="book-week-content-cell">
                <div class="cell-text" v-html="formatInline(weekRow.content)"></div>
              </td>

              <template v-if="weekIndex === 0">
                <td
                  :rowspan="group.weeks.length"
                  class="book-week-phonics-cell"
                >
                  <div
                    v-for="(item, itemIndex) in getBookWeekListLines(group.phonics)"
                    :key="`phonics-${itemIndex}`"
                    class="book-week-list-item"
                    v-html="formatInline(item)"
                  ></div>
                </td>

                <td
                  :rowspan="group.weeks.length"
                  class="book-week-hfw-cell"
                >
                  <div
                    class="book-week-hfw-text"
                    v-html="formatInline(group.highFrequencyWords)"
                  ></div>
                </td>
              </template>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Course A / B / C：English 3D 專用資訊卡；其他表格模式不受影響 -->
      <div v-else-if="tableData.hasCourseGroups" class="course-table-group">
        <section
          v-for="(course, courseIndex) in tableData.courseGroups"
          :key="`course-${course.title}-${courseIndex}`"
          class="course-block"
        >
          <div class="course-heading-row">
            <div class="course-title-wrap">
              <h3 v-html="formatInline(course.title)"></h3>
              <div v-if="course.grade" class="course-grade">{{ course.grade }}</div>
            </div>

            <div v-if="course.description.length" class="course-description">
              <div
                v-for="(item, itemIndex) in course.description"
                :key="`course-desc-${courseIndex}-${itemIndex}`"
                class="course-description-item"
                v-html="formatInline(item)"
              ></div>
            </div>
          </div>

          <!-- English 3D：教材＋文本比例融合成同一個表格 -->
          <div class="course-combined-table">
            <div class="course-material-header">
              <div>
                <strong>Issue Book</strong>
                <span>時事議題讀本</span>
              </div>
              <div>
                <strong>Language &amp; Writing Portfolio</strong>
                <span>英文寫作練習本</span>
              </div>
            </div>

            <div class="course-material-body">
              <div class="course-material-images">
                <button
                  v-for="(image, imageIndex) in course.issueImages"
                  :key="`issue-${courseIndex}-${imageIndex}`"
                  type="button"
                  class="course-cover-button"
                  @click="openImage(image, `${course.title} Issue Book`)"
                >
                  <img
                    :src="image"
                    :alt="`${course.title} Issue Book`"
                    class="course-cover-image"
                    loading="lazy"
                    @error="handleImageError"
                  />
                  <span class="course-cover-caption">
                    {{
                      course.title === "Course C"
                        ? "Course C"
                        : `${course.title} Volume ${imageIndex + 1}`
                    }}
                  </span>
                </button>
              </div>

              <div class="course-material-images">
                <button
                  v-for="(image, imageIndex) in course.writingImages"
                  :key="`writing-${courseIndex}-${imageIndex}`"
                  type="button"
                  class="course-cover-button"
                  @click="openImage(image, `${course.title} Language & Writing Portfolio`)"
                >
                  <img
                    :src="image"
                    :alt="`${course.title} Language & Writing Portfolio`"
                    class="course-cover-image"
                    loading="lazy"
                    @error="handleImageError"
                  />
                  <span class="course-cover-caption">
                    {{
                      course.title === "Course C"
                        ? "Course C"
                        : `${course.title} Volume ${imageIndex + 1}`
                    }}
                  </span>
                </button>
              </div>
            </div>

            <table class="curriculum-table course-table">
              <colgroup>
                <col style="width: 78%" />
                <col style="width: 22%" />
              </colgroup>
              <thead>
                <tr>
                  <th
                    v-for="(header, headerIndex) in course.headers"
                    :key="`course-header-${courseIndex}-${headerIndex}`"
                    v-html="formatInline(header)"
                  ></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in course.rows"
                  :key="`course-row-${courseIndex}-${rowIndex}`"
                >
                  <td
                    v-for="(cell, cellIndex) in row"
                    :key="`course-cell-${courseIndex}-${rowIndex}-${cellIndex}`"
                    v-html="formatInline(cell)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <!-- 原本兩種表格：完全保留 -->
      <table
        v-else-if="!tableData.hasSections"
        class="curriculum-table"
        :class="{
          'has-inline-list': hasInlineList,
          'image-heavy-table': hasImageHeavyTable,
          'into-science-focus-table':
            tableData.headers.length === 3 &&
            ['年級', 'Grade'].includes(String(tableData.headers[0] || '').trim()) &&
            ['學習主軸', 'Learning Focus'].includes(String(tableData.headers[1] || '').trim()) &&
            ['核心內容', 'Key Content'].includes(String(tableData.headers[2] || '').trim()),
        }"
        :style="hasImageHeavyTable ? { minWidth: imageTableMinWidth } : null"
      >
        <thead>
          <tr>
            <th
              v-for="(header, index) in tableData.headers"
              :key="`${header}-${index}`"
              v-html="formatInline(header)"
            ></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, rowIndex) in tableData.rows" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :class="{ 'is-first-column': cellIndex === 0 }"
            >
              <template
                v-for="(line, lineIndex) in getCellLines(cell)"
                :key="lineIndex"
              >
                <div v-if="line.type === 'image'" class="cell-image-wrap">
                  <img
                    :src="line.src"
                    :alt="line.alt || line.caption || 'Curriculum image'"
                    class="cell-image"
                    loading="lazy"
                    @click="
                      openImage(
                        line.src,
                        line.alt || line.caption || 'Curriculum image',
                      )
                    "
                    @error="handleImageError"
                  />

                  <div
                    v-if="line.caption"
                    class="cell-image-caption"
                    v-html="formatInline(line.caption)"
                  ></div>
                </div>

                <div
                  v-else-if="line.type === 'h1'"
                  class="cell-heading cell-heading-large"
                  v-html="formatInline(line.text)"
                ></div>

                <div
                  v-else-if="line.type === 'h2'"
                  class="cell-heading cell-heading-medium"
                  v-html="formatInline(line.text)"
                ></div>

                <div
                  v-else-if="line.type === 'h3'"
                  class="cell-heading cell-heading-small"
                  v-html="formatInline(line.text)"
                ></div>

                <div
                  v-else-if="line.type === 'list'"
                  class="cell-list-item"
                  v-html="formatInline(line.text)"
                ></div>

                <div
                  v-else-if="
                    cellIndex > 0 && getInlineListItems(line.text).length > 1
                  "
                  class="cell-inline-list"
                >
                  <span
                    v-for="(item, itemIndex) in getInlineListItems(line.text)"
                    :key="itemIndex"
                    class="cell-inline-list-item"
                    v-html="formatInline(item)"
                  ></span>
                </div>

                <div
                  v-else
                  class="cell-text"
                  v-html="formatInline(line.text)"
                ></div>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 第三種：Write Source 這類「區段＋教材圖片＋學習方向」 -->
      <div v-else class="section-table-group">
        <table
          v-for="(section, sectionIndex) in tableData.sections"
          :key="`${section.title}-${sectionIndex}`"
          class="curriculum-table section-table"
          :class="{
            'section-two-cols': section.columnCount === 2,
            'section-three-cols': section.columnCount >= 3,
          }"
        >
          <!--
            直接用 colgroup 控制欄寬。
            兩欄：教材 25% / 學習方向 75%
            三欄：Handbook 20% / Skills Book 20% / 學習方向 60%
          -->
          <colgroup v-if="section.columnCount === 2">
            <col style="width: 25%" />
            <col style="width: 75%" />
          </colgroup>

          <colgroup v-else>
            <col style="width: 20%" />
            <col style="width: 20%" />
            <col style="width: 60%" />
          </colgroup>

          <tbody>
            <!-- 黃底整列 section 標題 -->
            <tr class="section-title-row">
              <td
                :colspan="section.columnCount"
                class="section-title-cell"
                v-html="formatInline(section.title)"
              ></td>
            </tr>

            <!-- 欄位標題 -->
            <tr v-if="section.headers.length" class="section-header-row">
              <th
                v-for="(header, headerIndex) in section.headers"
                :key="headerIndex"
                v-html="formatMultiline(header)"
              ></th>
            </tr>

            <!--
              內容列：
              最後一欄是「學習方向」，每一行各自顯示；
              前面的教材欄位只在第一列出現，並 rowspan 跨越所有學習方向。
            -->
            <tr
              v-for="(row, rowIndex) in section.rows"
              :key="rowIndex"
              class="section-content-row"
            >
              <template v-if="rowIndex === 0">
                <td
                  v-for="fixedIndex in section.fixedColumnCount"
                  :key="`fixed-${fixedIndex}`"
                  :rowspan="section.rows.length"
                  class="section-book-cell"
                >
                  <template
                    v-for="(line, lineIndex) in getCellLines(
                      row.cells[fixedIndex - 1],
                      true,
                    )"
                    :key="lineIndex"
                  >
                    <div v-if="line.type === 'image'" class="cell-image-wrap">
                      <img
                        :src="line.src"
                        :alt="line.alt || line.caption || 'Curriculum image'"
                        class="cell-image"
                        loading="lazy"
                        @click="
                          openImage(
                            line.src,
                            line.alt || line.caption || 'Curriculum image',
                          )
                        "
                        @error="handleImageError"
                      />

                      <div
                        v-if="line.caption"
                        class="cell-image-caption"
                        v-html="formatInline(line.caption)"
                      ></div>
                    </div>

                    <div
                      v-else
                      class="cell-text"
                      v-html="formatInline(line.text)"
                    ></div>
                  </template>
                </td>
              </template>

              <td class="section-learning-cell">
                <template
                  v-for="(line, lineIndex) in getCellLines(
                    row.cells[section.columnCount - 1],
                    true,
                  )"
                  :key="lineIndex"
                >
                  <div
                    v-if="line.type === 'list'"
                    class="cell-list-item"
                    v-html="formatInline(line.text)"
                  ></div>

                  <div
                    v-else
                    class="cell-text"
                    v-html="formatInline(line.text)"
                  ></div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p
      v-if="tableData.note"
      class="curriculum-note"
      v-html="formatMultiline(tableData.note)"
    ></p>

    <p class="mobile-scroll-hint">← 左右滑動查看完整表格 →</p>

    <!-- 所有表格圖片共用放大燈箱 -->
    <div
      v-if="imageLightbox.visible"
      class="image-lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="imageLightbox.alt || '圖片放大預覽'"
      @click.self="closeImage"
    >
      <div class="image-lightbox-panel">
        <button
          type="button"
          class="image-lightbox-close"
          aria-label="關閉圖片預覽"
          title="關閉"
          @click="closeImage"
        >
          ×
        </button>

        <img
          :src="imageLightbox.src"
          :alt="imageLightbox.alt || '圖片放大預覽'"
          class="image-lightbox-content"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { computed, reactive } from "vue";

export default {
  name: "CurriculumTable",

  props: {
    data: { type: String, default: "" },
    defaultTitle: { type: String, default: "" },
    eyebrow: { type: String, default: "" },
  },

  setup(props) {
    const normalizeLine = (line) =>
      String(line || "")
        .replace(/\u00a0/g, " ")
        .trim();

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    // Google Sheet 支援：
    // **粗體**、*斜體*、{red}紅色{/red}、{big}大字{/big}、{mark}螢光標示{/mark}
    const formatInline = (value) => {
      let html = escapeHtml(value);

      const richTags = {
        red: "rich-red",
        blue: "rich-blue",
        green: "rich-green",
        orange: "rich-orange",
        yellow: "rich-yellow",
        purple: "rich-purple",
        gray: "rich-gray",
        big: "rich-big",
        small: "rich-small",
        mark: "rich-mark",
      };

      Object.entries(richTags).forEach(([tag, className]) => {
        const pattern = new RegExp(
          `\\{${tag}\\}([\\s\\S]+?)\\{\\/${tag}\\}`,
          "gi",
        );

        html = html.replace(pattern, `<span class="${className}">$1</span>`);
      });

      // 粗體
      html = html.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");

      // 斜體
      html = html.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");

      // Google Sheet 自訂換行符號 ↵
      html = html.replace(/↵/g, "<br>");

      return html;
    };
    const formatMultiline = (value) =>
      String(value || "")
        .split("\n")
        .map((line) => formatInline(line))
        .join("<br>");

    const splitColumns = (line) =>
      normalizeLine(line)
        .split(/[｜|]/)
        .map((cell) => cell.trim());

    // 第三種表格的區段標記。
    // 直接辨識完整 section 標記，不再先刪括號，避免
    // 「【section】G1」被變成「sectionG1」而判斷失敗。
    const getSectionMatch = (line) =>
      normalizeLine(line).match(
        /^\s*(?:【section】|\[section\]|［section］|section)\s*(.+?)\s*$/i,
      );

    const isSectionLine = (line) => Boolean(getSectionMatch(line));

    const getSectionTitle = (line) => {
      const match = getSectionMatch(line);
      return match ? match[1].trim() : "";
    };

    const resolveCourseImageUrl = (value) => {
      const image = String(value || "").trim();
      if (!image) return "";
      if (/^(https?:)?\/\//i.test(image) || image.startsWith("data:")) {
        return image;
      }
      return `${process.env.BASE_URL}${image.replace(/^\//, "")}`;
    };

    const splitCourseImages = (value) =>
      String(value || "")
        .split(/[,，、]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .map(resolveCourseImageUrl);

    // English 3D 封面預設路徑：
    // Sheet 沒有另外填圖片時，直接使用 public/images/.../cover 內現有封面。
    const english3DCovers = {
      "Course A": [
        "/images/img-1/Product/2-Reading/2-13English3DG4-12/cover/English3D_CourseA_V1.png",
        "/images/img-1/Product/2-Reading/2-13English3DG4-12/cover/English3D_CourseA_V2.png",
      ],
      "Course B": [
        "/images/img-1/Product/2-Reading/2-13English3DG4-12/cover/English3D_CourseB_V1.png",
        "/images/img-1/Product/2-Reading/2-13English3DG4-12/cover/English3D_CourseB_V2.png",
      ],
      "Course C": [
        "/images/img-1/Product/2-Reading/2-13English3DG4-12/cover/English3D_CourseC.png",
      ],
    };

    const getDefaultCourseImages = (courseTitle) =>
      (english3DCovers[courseTitle] || []).map(resolveCourseImageUrl);

    const getCourseMeta = (line) => {
      const value = normalizeLine(line);
      let match = value.match(/^(?:年級|Grade)\s*[：:]\s*(.+)$/i);
      if (match) return { type: "grade", value: match[1].trim() };

      match = value.match(/^(?:Issue Book|時事議題讀本)\s*[：:]\s*(.+)$/i);
      if (match) return { type: "issue", value: match[1].trim() };

      match = value.match(
        /^(?:Language\s*&\s*Writing\s*Portfolio|英文寫作練習本|Writing Portfolio)\s*[：:]\s*(.+)$/i,
      );
      if (match) return { type: "writing", value: match[1].trim() };

      return null;
    };

    const tableData = computed(() => {
      const raw = String(props.data || "").trim();

      if (!raw) {
        return {
          title: "",
          description: "",
          note: "",
          headers: [],
          rows: [],
          sections: [],
          hasSections: false,
          columnCount: 0,
          hasGoMath: false,
          goMathSections: [],
          hasCourseGroups: false,
          courseGroups: [],
        };
      }

      const lines = raw
        .split(/\r?\n/)
        .map(normalizeLine)
        .filter(Boolean)
        .filter((line) => !/^-{3,}$/.test(line));

      let title = "";
      let description = "";

      // Course A / B / C 這類「標題＋說明＋獨立表格」格式。
      // 只在同一欄位出現兩個以上 【...】群組時啟用，避免影響舊的單表格資料。
      const genericGroupTitle = (line) => {
        const match = normalizeLine(line).match(/^【(.+?)】$/);
        return match && !isSectionLine(line) ? match[1].trim() : "";
      };
      const genericGroupCount = lines.filter((line) => genericGroupTitle(line)).length;
      const hasCourseGroups =
        genericGroupCount >= 2 && lines.some((line) => /[｜|]/.test(line));

      if (hasCourseGroups) {
        const courseGroups = [];
        let current = null;

        const pushCourse = () => {
          if (!current) return;

          const defaultImages = getDefaultCourseImages(current.title);
          if (!current.issueImages.length) {
            current.issueImages = [...defaultImages];
          }
          // 目前 Writing Portfolio 尚無正式封面：
          // 暫時用同 Course 的 Issue Book 圖片預覽位置與尺寸。
          if (!current.writingImages.length) {
            current.writingImages = [...current.issueImages];
          }

          if (current.headers.length && current.rows.length) courseGroups.push(current);
          current = null;
        };

        lines.forEach((line) => {
          const groupTitle = genericGroupTitle(line);
          if (groupTitle) {
            pushCourse();
            current = {
              title: groupTitle,
              grade: "",
              issueImages: [],
              writingImages: [],
              description: [],
              headers: [],
              rows: [],
              tableStarted: false,
            };
            return;
          }

          if (!current || line.startsWith("※")) return;

          if (!current.tableStarted) {
            const meta = getCourseMeta(line);
            if (meta) {
              if (meta.type === "grade") current.grade = meta.value;
              if (meta.type === "issue") {
                current.issueImages = splitCourseImages(meta.value);
              }
              if (meta.type === "writing") {
                current.writingImages = splitCourseImages(meta.value);
              }
              return;
            }
          }

          if (/[｜|]/.test(line)) {
            const cells = splitColumns(line);
            if (!current.tableStarted) {
              current.headers = cells;
              current.tableStarted = true;
            } else {
              while (cells.length < current.headers.length) cells.push("");
              current.rows.push(cells.slice(0, current.headers.length));
            }
            return;
          }

          if (!current.tableStarted) {
            current.description.push(line.replace(/^[■▪•●]\s*/, "").trim());
          }
        });

        pushCourse();

        if (courseGroups.length) {
          return {
            title: "",
            description: "",
            note: lines.filter((line) => line.startsWith("※")).join("\n"),
            headers: [],
            rows: [["course-groups"]],
            sections: [],
            hasSections: false,
            columnCount: 2,
            hasGoMath: false,
            goMathSections: [],
            hasCourseGroups: true,
            courseGroups,
          };
        }
      }

      // 第一行仍沿用原本的 【表格標題】
      if (lines[0] && /^【.+】$/.test(lines[0]) && !isSectionLine(lines[0])) {
        title = lines
          .shift()
          .replace(/^【|】$/g, "")
          .trim();
      }

      if (
        lines[0]?.startsWith("說明：") ||
        lines[0]?.startsWith("Description:")
      ) {
        description = lines
          .shift()
          .replace(/^(說明：|Description:)/, "")
          .trim();
      }

      const noteLines = lines.filter((line) => line.startsWith("※"));
      const note = noteLines.join("\n");
      const tableLines = lines.filter((line) => !line.startsWith("※"));

      // --------------------------------------------------
      // 第三種表格：
      // 只要內容中出現 section 標記就自動啟用，
      // 不需要額外寫 layout / row / subheader。
      // 支援 【section】、[section]、［section］
      //
      // 【section】G1 Write One
      // Handbook｜Skills Book｜學習方向
      // {img}...{/img}｜{img}...{/img}｜文字
      // --------------------------------------------------
      // Go Math 專用格式：
      // 【gomath】Kindergarten
      // {img}...{/img}↵**Kindergarten**↵共 10 本
      // Volume 1: Ch 1-2｜Volume 2: Ch 3-4｜...
      const getGoMathMatch = (line) =>
        normalizeLine(line).match(
          /^\s*(?:【gomath】|\[gomath\]|［gomath］|gomath)\s*(.+?)\s*$/i,
        );
      const isGoMathLine = (line) => Boolean(getGoMathMatch(line));
      const hasGoMath = tableLines.some((line) => isGoMathLine(line));

      if (hasGoMath) {
        const goMathSections = [];
        let current = null;

        const pushGoMath = () => {
          if (!current) return;
          current.maxColumns = Math.min(5, Math.max(1, current.volumes.length));
          goMathSections.push(current);
          current = null;
        };

        tableLines.forEach((line) => {
          const match = getGoMathMatch(line);
          if (match) {
            pushGoMath();
            current = {
              title: match[1].trim(),
              bookCell: "",
              volumes: [],
              maxColumns: 5,
            };
            return;
          }
          if (!current) return;
          if (!current.bookCell) {
            current.bookCell = line;
            return;
          }
          splitColumns(line)
            .filter(Boolean)
            .forEach((item) => {
              const parts = item.split(/[:：]/);
              current.volumes.push({
                name: (parts.shift() || "").trim(),
                chapter: parts.join(":").trim(),
              });
            });
        });
        pushGoMath();

        return {
          title,
          description,
          note,
          headers: [],
          rows: goMathSections.length ? [["gomath"]] : [],
          sections: [],
          hasSections: false,
          columnCount: 0,
          hasGoMath: true,
          goMathSections,
          hasCourseGroups: false,
          courseGroups: [],
        };
      }

      const hasSections = tableLines.some((line) => isSectionLine(line));

      if (hasSections) {
        const sections = [];
        let currentSection = null;

        const pushCurrentSection = () => {
          if (!currentSection) return;

          const columnCount = Math.max(
            currentSection.headers.length,
            ...currentSection.rows.map((row) => row.cells.length),
            2,
          );

          currentSection.columnCount = columnCount;
          currentSection.fixedColumnCount = Math.max(columnCount - 1, 1);

          // 每列補齊欄位，確保最後一欄永遠是「學習方向」。
          currentSection.rows = currentSection.rows.map((row) => {
            const cells = [...row.cells];
            while (cells.length < columnCount) cells.push("");

            return {
              ...row,
              cells: cells.slice(0, columnCount),
            };
          });

          sections.push(currentSection);
          currentSection = null;
        };

        tableLines.forEach((line) => {
          if (isSectionLine(line)) {
            pushCurrentSection();

            currentSection = {
              title: getSectionTitle(line),
              headers: [],
              rows: [],
              columnCount: 0,
              fixedColumnCount: 0,
              waitingForHeader: true,
            };

            return;
          }

          if (!currentSection) return;

          const cells = splitColumns(line);

          if (currentSection.waitingForHeader) {
            currentSection.headers = cells;
            currentSection.waitingForHeader = false;
            return;
          }

          currentSection.rows.push({
            type: "row",
            cells,
          });
        });

        pushCurrentSection();

        return {
          title,
          description,
          note,
          headers: [],
          rows: sections.flatMap((section) => section.rows),
          sections,
          hasSections: true,
          columnCount: Math.max(
            0,
            ...sections.map((section) => section.columnCount),
          ),
          hasGoMath: false,
          goMathSections: [],
          hasCourseGroups: false,
          courseGroups: [],
        };
      }

      // --------------------------------------------------
      // 原本兩種表格：完全維持原邏輯。
      // --------------------------------------------------
      const headers = tableLines.length ? splitColumns(tableLines.shift()) : [];

      const rows = tableLines
        .map(splitColumns)
        .filter((row) => row.some(Boolean))
        .map((row) => {
          const normalized = [...row];

          while (normalized.length < headers.length) {
            normalized.push("");
          }

          return normalized.slice(0, headers.length);
        });

      return {
        title,
        description,
        note,
        headers,
        rows,
        sections: [],
        hasSections: false,
        columnCount: headers.length,
        hasGoMath: false,
        goMathSections: [],
        hasCourseGroups: false,
        courseGroups: [],
      };
    });

    const getCellLines = (value, emptyAsBlank = false) => {
      const text = String(value ?? "").trim();

      if (!text) {
        return emptyAsBlank ? [] : [{ text: "—", type: "text" }];
      }

      return text
        .split(/↵|<br\s*\/?>|\n/i)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          // Google Sheet 圖片格式：
          // {img}圖片網址{/img}
          // {img alt="Grade 1 Book 1"}圖片網址{/img}
          // {img caption="Modules 1–2"}圖片網址{/img}
          const imageMatch = line.match(
            /^\{img(?:\s+(alt|caption)=(?:""|["'])(.+?)(?:""|["']))?\}([\s\S]+?)\{\/img\}$/i,
          );

          if (imageMatch) {
            const attributeType = imageMatch[1]?.toLowerCase();
            const attributeValue = imageMatch[2]?.trim() || "";

            return {
              type: "image",
              src: imageMatch[3]?.trim() || "",
              alt: attributeValue,
              caption: attributeType === "caption" ? attributeValue : "",
            };
          }

          const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
          if (headingMatch) {
            return {
              text: headingMatch[2].trim(),
              type: `h${headingMatch[1].length}`,
            };
          }

          const listMatch = line.match(/^(?:[ㆍ・•●▪◦‧-]|\d+[.)])\s+(.+)$/);
          if (listMatch) return { text: listMatch[1].trim(), type: "list" };

          return { text: line, type: "text" };
        });
    };

    // Sheet 手動分項規則：
    // 只有「・」才拆成列表。
    // 「、」、中文逗號「，」、英文逗號「,」都保留為一般標點，不拆分。
    const getInlineListItems = (value) => {
      const text = String(value ?? "").trim();
      if (!text || !text.includes("・")) return [];

      return text
        .split(/\s*・\s*/)
        .map((item) => item.trim())
        .filter(Boolean);
    };

    // Start Right Reader 類型專用：只有欄位完全符合時才啟用，
    // 避免影響既有一般表格、圖片表格、Section 與 Go Math。
    const isBookWeekTable = computed(() => {
      if (tableData.value.hasSections || tableData.value.hasGoMath) return false;

      const headers = tableData.value.headers.map((header) =>
        String(header || "").replace(/\s+/g, "").toLowerCase(),
      );

      const expected = [
        "book",
        "書本",
        "週",
        "內容",
        "phonics",
        "high-frequencywords",
      ];

      return (
        headers.length === expected.length &&
        headers.every((header, index) => header === expected[index])
      );
    });

    // Start Right Reader Gr.K 專用：依小班／中班／大班分區上底色。
    // 僅鎖定 Gr.K，避免其他 Book + Week 表格受到影響。
    const isKindergartenBookWeekTable = computed(() => {
      if (!isBookWeekTable.value) return false;

      const title = String(tableData.value.title || props.defaultTitle || "")
        .replace(/\s+/g, "")
        .toLowerCase();

      return (
        title.includes("startrightreadergr.k") ||
        title.includes("startrightreadergrk") ||
        title.includes("startrightreaderk")
      );
    });

    const bookWeekGroups = computed(() => {
      if (!isBookWeekTable.value) return [];

      const groups = [];
      let current = null;

      tableData.value.rows.forEach((row) => {
        const [book = "", cover = "", week = "", content = "", phonics = "", hfw = ""] = row;
        const startsNewGroup = Boolean(String(book).trim() || String(cover).trim());

        if (startsNewGroup || !current) {
          if (current) groups.push(current);

          current = {
            book,
            cover,
            phonics,
            highFrequencyWords: hfw,
            weeks: [],
          };
        }

        current.weeks.push({ week, content });

        // 若後續列有補資料，也允許補回群組欄位。
        if (!current.phonics && phonics) current.phonics = phonics;
        if (!current.highFrequencyWords && hfw) current.highFrequencyWords = hfw;
      });

      if (current) groups.push(current);
      return groups;
    });

    const getBookWeekListLines = (value) =>
      String(value ?? "")
        .split(/↵|<br\s*\/?>|\n/i)
        .map((line) => line.trim().replace(/^[*•・●▪◦‧-]+\s*/, ""))
        .filter(Boolean);

    // 只要表格中真的存在「列表型內容」，才啟用特殊版型。
    // 一般表格（例如 Literary Themes 四欄表）維持原本欄寬。
    const hasInlineList = computed(() => {
      // 舊的 has-inline-list 欄寬只適用兩欄文字表。
      // Into Science 是三欄表，不能把第一欄強制成 240px。
      if (tableData.value.hasSections || tableData.value.columnCount !== 2) return false;

      return tableData.value.rows.some((row) =>
        row.some((cell, cellIndex) => {
          if (cellIndex === 0) return false;

          return getCellLines(cell).some(
            (line) =>
              line.type === "text" && getInlineListItems(line.text).length > 1,
          );
        }),
      );
    });

    // 自動判斷「多欄圖片教材表格」：
    // 例如 Into Reading Grade 1–6 這種 Book 1～Book 5 多欄封面表格。
    // 手機版不要硬擠進 720px，而是保留較寬版面後左右滑動。
    const hasImageHeavyTable = computed(() => {
      if (tableData.value.hasSections) return false;
      if (tableData.value.columnCount < 4) return false;

      return tableData.value.rows.some((row) =>
        row.some((cell) =>
          getCellLines(cell, true).some((line) => line.type === "image"),
        ),
      );
    });

    // 圖片型表格依欄數自動決定最小寬度：
    // Grade 105px + 每個 Book / Volume 至少 110px。
    // 5～8 欄通常可自然填滿桌機；9～10 欄以上才進入水平捲動。
    const imageTableMinWidth = computed(() => {
      const contentColumns = Math.max(
        (tableData.value.columnCount || 1) - 1,
        1,
      );
      return `${105 + contentColumns * 110}px`;
    });

    // 所有表格圖片共用燈箱
    const imageLightbox = reactive({
      visible: false,
      src: "",
      alt: "",
    });

    const openImage = (src, alt = "") => {
      if (!src) return;

      imageLightbox.src = src;
      imageLightbox.alt = alt;
      imageLightbox.visible = true;
    };

    const closeImage = () => {
      imageLightbox.visible = false;
      imageLightbox.src = "";
      imageLightbox.alt = "";
    };

    const handleImageError = (event) => {
      const image = event.target;
      image.style.display = "none";

      const wrapper = image.closest(".cell-image-wrap");
      if (wrapper) wrapper.classList.add("is-image-error");
    };

    return {
      tableData,
      isBookWeekTable,
      isKindergartenBookWeekTable,
      bookWeekGroups,
      getBookWeekListLines,
      hasInlineList,
      hasImageHeavyTable,
      imageTableMinWidth,
      getCellLines,
      getInlineListItems,
      formatInline,
      formatMultiline,
      imageLightbox,
      openImage,
      closeImage,
      handleImageError,
    };
  },
};
</script>

<style scoped>
/* =========================================================
   CurriculumTable：統一表格視覺規則
   原則：
   1. 所有表格共用顏色、字級、框線、padding、圖片尺寸
   2. 只有「欄寬 / 排版方式」依表格類型做必要差異
   3. 手機版統一由外層水平捲動，不再各自硬塞不同尺寸
   ========================================================= */

.curriculum-section {
  --table-blue: #174f7d;
  --table-blue-light: #f1f6f9;
  --table-border: #e2e8ed;
  --table-border-strong: #dfe7ed;
  --table-text: #4d5d68;
  --table-muted: #647381;
  --table-hover: #f7fafc;
  --table-book-bg: #f8fbfd;

  --table-font-size: 14px;
  --table-header-font-size: 15px;
  --table-cell-padding-y: 6px;
  --table-cell-padding-x: 8px;

  --table-image-width: 100px;
  --table-image-height: 140px;
  --table-image-gap: 5px;

  width: min(100%, 1040px);
  margin: 70px auto 10px;
}

.curriculum-heading {
  margin-bottom: 28px;
  text-align: center;
}

.curriculum-eyebrow {
  display: block;
  margin-bottom: 9px;
  color: #24618d;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.curriculum-heading h2 {
  margin: 0;
  color: #173f5f;
  font-size: clamp(25px, 3vw, 36px);
  line-height: 1.3;
}

.curriculum-heading p {
  width: min(100%, 680px);
  margin: 12px auto 0;
  color: var(--table-muted);
  line-height: 1.8;
}

/* ---------- Course A / B / C English 3D 融合表格 ---------- */
.course-table-group {
  display: grid;
  gap: 46px;
}

.course-block {
  display: grid;
  gap: 20px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
}

.course-heading-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
}

.course-title-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 auto;
}

.course-title-wrap h3 {
  margin: 0;
  color: #173f5f;
  font-size: clamp(28px, 3vw, 38px);
  font-weight: 750;
  line-height: 1.1;
}

.course-grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 82px;
  padding: 6px 15px;
  border-radius: 999px;
  background: #edf4f8;
  color: #244f70;
  font-size: 16px;
  font-weight: 700;
}

.course-description {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 22px;
  padding-top: 0;
  color: #40586b;
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
}

.course-description-item {
  position: relative;
  padding-left: 15px;
}

.course-description-item::before {
  content: "■";
  position: absolute;
  top: 0.12em;
  left: 0;
  color: #285f87;
  font-size: 0.62em;
}

/* 教材區與文本比例視覺上是一張完整表格 */
.course-combined-table {
  overflow: hidden;
  border: 0;
  border-radius: 0;
  background: #fff;
}

.course-material-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #1f5b87;
  color: #fff;
}

.course-material-header > div {
  display: flex;
  min-height: 58px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 9px 14px;
  text-align: center;
}

.course-material-header > div + div {
  border-left: 1px solid rgba(255,255,255,.3);
}

.course-material-header strong {
  font-size: 15px;
  line-height: 1.25;
}

.course-material-header span {
  margin-top: 3px;
  font-size: 13px;
  line-height: 1.25;
}

.course-material-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 225px;
  border-bottom: 1px solid #dfe7ee;
}

.course-material-body > div + div {
  border-left: 1px solid #dfe7ee;
}

.course-material-images {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 20px 18px;
}

.course-cover-button {
  appearance: none;
  width: min(38%, 145px);
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: zoom-in;
}

.course-cover-image {
  display: block;
  width: 100%;
  max-height: 185px;
  object-fit: contain;
  filter: drop-shadow(0 5px 7px rgba(24,49,67,.12));
  transition: transform .2s ease;
}

.course-cover-button:hover .course-cover-image {
  transform: translateY(-2px);
}

/* 下半部沿用同一張表，不再有第二層外框 */
.course-combined-table .course-table {
  width: 100%;
  min-width: 0 !important;
  margin: 0;
  border: 0;
  border-radius: 0;
}

.course-table th {
  padding: 11px 16px;
  background: #1f5b87;
  color: #fff;
  font-weight: 700;
  text-align: left;
}

.course-table th:last-child {
  text-align: center;
}

.course-table td {
  padding: 11px 16px;
  color: #40586b;
  text-align: left;
  vertical-align: middle;
}

.course-table td:last-child {
  color: #173f5f;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .course-heading-row {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .course-description {
    justify-content: center;
    padding-top: 0;
  }
}

@media (max-width: 767px) {
  .course-table-group {
    gap: 38px;
  }

  .course-block {
    gap: 16px;
  }

  .course-heading-row {
    align-items: center;
  }

  .course-title-wrap {
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .course-title-wrap h3 {
    font-size: 29px;
  }

  .course-grade {
    min-width: 72px;
    padding: 5px 13px;
    font-size: 14px;
  }

  .course-description {
    display: grid;
    width: fit-content;
    max-width: 100%;
    margin: 0 auto;
    gap: 7px;
    font-size: 13px;
  }

  .course-combined-table {
    border-radius: 0;
  }

  .course-material-header > div {
    min-height: 62px;
    padding: 8px 6px;
  }

  .course-material-header strong {
    font-size: 12px;
  }

  .course-material-header span {
    font-size: 11px;
  }

  .course-material-body {
    min-height: 155px;
  }

  .course-material-images {
    gap: 6px;
    padding: 14px 7px;
  }

  .course-cover-button {
    width: min(46%, 82px);
  }

  .course-cover-image {
    max-height: 125px;
  }

  .course-combined-table {
    display: block;
    width: 100%;
    max-width: none;
    border-radius: 0;
  }

  .course-combined-table .course-table {
    display: table;
    width: 100% !important;
    max-width: none !important;
    min-width: 100% !important;
    table-layout: fixed !important;
    font-size: 12px;
  }

  .course-combined-table .course-table col:first-child {
    width: 78% !important;
  }

  .course-combined-table .course-table col:last-child {
    width: 22% !important;
  }

  .course-combined-table .course-table th,
  .course-combined-table .course-table td {
    padding: 9px 8px;
  }

  .course-combined-table .course-table th:first-child,
  .course-combined-table .course-table td:first-child {
    width: 78% !important;
  }

  .course-combined-table .course-table th:last-child,
  .course-combined-table .course-table td:last-child {
    width: 22% !important;
  }
}

/* ---------- 所有表格共用外框 ---------- */

.curriculum-table-scroll {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  border: 1px solid var(--table-border-strong);
  border-radius: 17px;
  background: #fff;
  box-shadow: 0 14px 38px rgba(28, 64, 91, 0.08);
}

/* 兩欄文字型表格不必撐滿 1040px，收斂後閱讀更舒服 */
.curriculum-table-scroll.compact-mode {
  width: min(100%, 940px);
}

/* 圖片教材表格維持主內容寬度；欄數過多時由 table 自己觸發水平捲動 */
.curriculum-table-scroll.image-mode {
  width: 100%;
}

/* ---------- 所有 table 共用基礎 ---------- */

.curriculum-table {
  width: 100%;
  min-width: 0;
  border-collapse: collapse;
  table-layout: auto;
  background: #fff;
}

.curriculum-table th,
.curriculum-table td {
  border-right: 1px solid var(--table-border);
  border-bottom: 1px solid var(--table-border);
  font-size: var(--table-font-size);
  line-height: 1.55;
  vertical-align: middle;
}
.curriculum-table th {
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  background: var(--table-blue);
  color: #fff;
  font-size: var(--table-header-font-size);
  font-weight: 750;
  text-align: center;
  white-space: nowrap;
}

.curriculum-table td {
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  color: var(--table-text);
  text-align: center;
  word-break: normal;
  overflow-wrap: break-word;
}

.curriculum-table th:last-child,
.curriculum-table td:last-child {
  border-right: 0;
}

.curriculum-table tbody tr:last-child td {
  border-bottom: 0;
}

.curriculum-table tbody tr {
  transition: background 0.18s ease;
}

.curriculum-table tbody tr:hover {
  background: var(--table-hover);
}

/* 第一欄：Grade / 類別 */
.curriculum-table td.is-first-column {
  background: var(--table-blue-light);
  color: var(--table-blue);
  font-weight: 700;
  white-space: nowrap;
}

/* ---------- 通用內容 ---------- */

.cell-text,
.cell-heading,
.cell-list-item {
  display: block;
}

.cell-heading {
  color: inherit;
  font-weight: 750;
  line-height: 1.4;
}

.cell-heading-large {
  margin: 8px 0;
  font-size: 20px;
}

.cell-heading-medium {
  margin: 7px 0;
  font-size: 17px;
}

.cell-heading-small {
  margin: 6px 0;
  font-size: 15px;
}

.cell-list-item {
  position: relative;
  padding-left: 1.15em;
  text-align: left;
}

.cell-list-item::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #24618d;
}

.cell-inline-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  width: 100%;
  text-align: left;
}

.cell-inline-list-item {
  position: relative;
  flex: 0 1 auto;
  max-width: 100%;
  padding-left: 0.9em;
  line-height: 1.7;
  text-align: left;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: normal;
}

.cell-inline-list-item::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #24618d;
}

/* ---------- 所有教材圖片共用尺寸 ---------- */

.cell-image-wrap {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--table-image-gap);
}

.cell-image {
  display: block;
  width: auto;
  /* max-width: 96px; */
  height: 125px;
  margin: 0 auto;
  border-radius: 7px;
  object-fit: contain;
  background: #fff;
  cursor: zoom-in;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.cell-image-caption {
  color: #425563;
  font-size: 13px;
  font-weight: 750;
  line-height: 1.35;
  text-align: center;
  white-space: normal;
}

.cell-image-wrap.is-image-error::before {
  content: "圖片無法載入";
  display: block;
  padding: 12px;
  border: 1px dashed #cbd5dc;
  border-radius: 6px;
  color: #8b98a2;
  font-size: 13px;
}

/* =========================================================
   Book + Week 合併表格（Start Right Reader 類型限定）
   ========================================================= */

.book-week-table {
  width: 100%;
  min-width: 980px;
  table-layout: fixed;
}

.book-week-book-col { width: 11%; }
.book-week-cover-col { width: 15%; }
.book-week-week-col { width: 12%; }
.book-week-content-col { width: 20%; }
.book-week-phonics-col { width: 25%; }
.book-week-hfw-col { width: 17%; }

.book-week-table td {
  padding: 8px 10px;
}

.book-week-table .book-week-group-start td {
  border-top: 2px solid #cfdae3;
}

.book-week-table tbody tr:first-child td {
  border-top: 0;
}

.book-week-book-cell {
  background: var(--table-blue-light);
  color: var(--table-blue) !important;
  font-weight: 750;
  text-align: center;
}

.book-week-cover-cell {
  background: #fff;
  text-align: center;
}

.book-week-cover-cell .cell-image {
  height: 118px;
  max-width: 100%;
}

.book-week-week-cell,
.book-week-content-cell {
  background: #fff;
  text-align: center;
}


/* Start Right Reader：固定補上「內容」與 Phonics 之間的垂直分隔線 */
.book-week-table .book-week-content-cell {
  border-right: 1px solid var(--table-border) !important;
}


.book-week-table .book-week-phonics-cell {
  border-left: 1px solid var(--table-border) !important;
}

.book-week-phonics-cell,
.book-week-hfw-cell {
  background: #fff;
  text-align: left !important;
  vertical-align: middle !important;
}

.book-week-list-item {
  position: relative;
  padding-left: 1em;
  line-height: 1.55;
  text-align: left;
}

.book-week-list-item + .book-week-list-item {
  margin-top: 3px;
}

.book-week-list-item::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #24618d;
}

.book-week-hfw-text {
  line-height: 1.7;
  text-align: left;
  word-break: keep-all;
  overflow-wrap: normal;
  white-space: normal;
}

/* Start Right Reader Gr.K：依班級區塊分色
   小班 Book 1–2：淡藍／中班 Book 3–6：白／大班 Book 7–9：淡藍 */
.book-week-table tbody tr.kindergarten-small > td,
.book-week-table tbody tr.kindergarten-large > td {
  background: var(--table-blue-light);
}

.book-week-table tbody tr.kindergarten-middle > td {
  background: #fff;
}

/* =========================================================
   一般表格
   ========================================================= */

/* 兩欄文字型：左欄固定、右欄吃剩餘空間 */
.curriculum-table.has-inline-list {
  width: 100%;
  table-layout: fixed;
}

.curriculum-table.has-inline-list th:first-child,
.curriculum-table.has-inline-list td:first-child {
  width: 240px;
}

.curriculum-table.has-inline-list th:last-child,
.curriculum-table.has-inline-list td:last-child {
  width: auto;
}

/* 圖片多欄表格：Grade 固定窄欄，Book / Volume 平均分配 */
.curriculum-table.image-heavy-table {
  width: 100%;
  table-layout: fixed;
}

.image-heavy-table th:first-child,
.image-heavy-table td:first-child {
  width: 105px;
  min-width: 105px;
  max-width: 105px;
}

.image-heavy-table th:not(:first-child),
.image-heavy-table td:not(:first-child) {
  width: auto;
  min-width: 110px;
}

/* =========================================================
   Section / Write Source
   共用同一套表格視覺，只保留欄位配置差異
   ========================================================= */

.section-table-group {
  width: 100%;
  min-width: 100%;
}

.section-table {
  width: 100%;
  min-width: 0;
  table-layout: fixed;
}

/* 區段標題與 Go Math 共用 */
.section-table .section-title-cell,
.gomath-table .section-title-cell {
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  border-bottom: 1px solid var(--table-border);
  background: var(--table-blue);
  color: #fff;
  font-size: var(--table-header-font-size);
  font-weight: 750;
  line-height: 1.45;
  text-align: center;
}

/* 次標題列 */
.section-table .section-header-row th {
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  border-right: 1px solid var(--table-border);
  border-bottom: 1px solid var(--table-border);
  background: var(--table-blue-light);
  color: var(--table-blue);
  font-size: var(--table-font-size);
  font-weight: 750;
  line-height: 1.5;
  text-align: center;
  white-space: normal;
}

.section-table .section-header-row th:last-child {
  border-right: 0;
}

.section-table .section-header-row + .section-content-row td {
  border-top: 0;
}

.section-table .section-content-row td {
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x);
  background: #fff;
}

.section-table .section-book-cell {
  background: var(--table-book-bg);
  text-align: center;
}

.section-table .section-learning-cell {
  background: #fff;
  color: var(--table-text);
  text-align: left;
}

.section-table .section-learning-cell .cell-text,
.section-table .section-learning-cell .cell-list-item {
  text-align: left;
}

/* 圖片尺寸沿用通用 .cell-image，不再另外放大 */

/* =========================================================
   Go Math
   視覺完全沿用共用規則，只保留 Volume grid 排版
   ========================================================= */
.gomath-table-group {
  width: max-content;
  min-width: 100%;
}
.gomath-table {
  width: max-content;
  min-width: 100%;
  table-layout: auto;
}

.gomath-book-col {
  width: 100px;
}

.gomath-book-cell {
  width: 100px;
  min-width: 100px;
  padding: var(--table-cell-padding-y) var(--table-cell-padding-x) !important;
  background: var(--table-book-bg);
  text-align: center;
  vertical-align: middle;
}

.gomath-book-cell .cell-text {
  margin-top: 2px;
  color: var(--table-blue);
  font-weight: 700;
}

.gomath-volumes-cell {
  padding: 0 !important;
  vertical-align: stretch !important;
}
.gomath-volume-grid {
  display: grid;
  grid-template-columns: repeat(var(--gomath-cols), minmax(108px, 1fr));
  width: 100%;
}

.gomath-volume-card {
  min-width: 105px;
  min-height: 58px;
  border-right: 1px solid var(--table-border);
  border-bottom: 1px solid var(--table-border);
  text-align: center;
}

.gomath-volume-card:last-child {
  border-right: 0;
}

.gomath-volume-name {
  padding: 5px;
  border-bottom: 1px solid var(--table-border);
  background: var(--table-blue-light);
  color: var(--table-blue);
  font-size: var(--table-font-size);
  font-weight: 750;
  line-height: 1.4;
}

.gomath-chapter {
  padding: 5px;
  color: var(--table-text);
  font-size: var(--table-font-size);
  font-weight: 650;
  line-height: 1.4;
}

/* ---------- Note ---------- */

.curriculum-note {
  margin: 14px 4px 0;
  color: #7b8792;
  font-size: 14px;
  line-height: 1.7;
  text-align: left;
}

.mobile-scroll-hint {
  display: none;
  margin: 10px 0 0;
  color: #82909c;
  font-size: 12px;
  text-align: center;
}

/* ---------- Rich text ---------- */

:deep(.rich-red) {
  color: #8f1d2c;
}

:deep(.rich-blue) {
  color: #1565c0;
}

:deep(.rich-green) {
  color: #2e7d32;
}

:deep(.rich-orange) {
  color: #d95f02;
}

:deep(.rich-yellow) {
  color: #c58a00;
}

:deep(.rich-purple) {
  color: #6a1b9a;
}

:deep(.rich-gray) {
  color: #757575;
}

:deep(.rich-big) {
  font-size: 1.25em;
  font-weight: 700;
}

:deep(.rich-small) {
  font-size: 0.82em;
}

:deep(.rich-mark) {
  padding: 0.08em 0.25em;
  border-radius: 0.2em;
  background: #fff1a8;
  color: inherit;
}

:deep(strong) {
  color: inherit;
  font-weight: 750;
}

:deep(em) {
  color: inherit;
  font-style: italic;
}

/* =========================================================
   Mobile
   所有表格採相同策略：保持可讀欄寬，超出時水平捲動
   ========================================================= */

@media (max-width: 768px) {
  .curriculum-section {
    --table-font-size: 13px;
    --table-header-font-size: 13px;
    --table-cell-padding-y: 5px;
    --table-cell-padding-x: 7px;
    --table-image-width: 95px;
    --table-image-height: 135px;

    margin-top: 50px;
  }

  .curriculum-table-scroll {
    display: block;
    border-radius: 14px;
    overflow-x: auto;
    overflow-y: visible;
    touch-action: pan-x pan-y;
    overscroll-behavior-x: contain;
    overscroll-behavior-y: auto;
  }

  .curriculum-table-scroll.compact-mode,
  .curriculum-table-scroll.image-mode {
    width: 100%;
  }

  /* Book + Week 合併表格：手機保持欄寬後水平滑動 */
  .book-week-table {
    width: 980px;
    min-width: 980px;
  }

  /* 一般文字表格 */
  .curriculum-table:not(.section-table):not(.image-heavy-table):not(
      .gomath-table
    ) {
    width: max-content;
    min-width: 720px;
  }

  /* 多欄圖片表格：保留 Vue 動態 min-width，超出才水平滑動 */
  .curriculum-table.image-heavy-table {
    width: 100%;
    table-layout: fixed;
  }

  /* Section 表格 */
  .section-table-group,
  .section-table {
    width: 720px;
    min-width: 720px;
  }

  /* Go Math */
  .gomath-table-group {
    width: max-content;
    min-width: 100%;
  }

  .gomath-table {
    width: max-content;
    min-width: 100%;
  }

  .gomath-volume-grid {
    grid-template-columns: repeat(var(--gomath-cols), minmax(108px, 1fr));
  }

  .cell-inline-list {
    gap: 6px 14px;
  }

  .curriculum-note {
    margin-top: 12px;
  }

  .mobile-scroll-hint {
    display: block;
  }
}

/* =========================================================
   所有表格圖片：共用點擊放大
   ========================================================= */

.cell-image {
  cursor: zoom-in;
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.cell-image:hover {
  transform: scale(1.03);
  opacity: 0.92;
}

.image-lightbox {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(0, 0, 0, 0.78);
}

.image-lightbox-panel {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 92vw;
  max-height: 88vh;
}

.image-lightbox-content {
  display: block;
  width: auto;
  max-width: 92vw;
  max-height: 88vh;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
}

.image-lightbox-close {
  position: absolute;
  z-index: 100000;
  top: -18px;
  right: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(23, 79, 125, 0.12);
  border-radius: 50%;
  background: #fff;
  color: #174f7d;
  font-family: Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32);
}

.image-lightbox-close:hover {
  background: #f1f6f9;
  transform: scale(1.05);
}

.image-lightbox-close:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.9);
  outline-offset: 3px;
}

@media (max-width: 768px) {
  .image-lightbox {
    padding: 20px;
  }

  .image-lightbox-panel {
    max-width: 92vw;
    max-height: 84vh;
  }

  .image-lightbox-content {
    max-width: 92vw;
    max-height: 84vh;
  }

  .image-lightbox-close {
    top: -14px;
    right: -10px;
    width: 40px;
    height: 40px;
    font-size: 28px;
  }
}

/* v7：English 3D 外層不顯示框線；只有實際表格有框 */
.course-table-group,
.course-block {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  background: transparent !important;
}

.course-combined-table {
  border: 1px solid #d7e1e8 !important;
  border-radius: 0 !important;
  overflow: hidden;
}

.course-material-header > div,
.course-material-body > div,
.course-combined-table .course-table th,
.course-combined-table .course-table td {
  border-color: #d7e1e8 !important;
}

.course-material-header > div + div,
.course-material-body > div + div {
  border-left: 1px solid #d7e1e8 !important;
}

.course-material-body {
  border-bottom: 1px solid #d7e1e8 !important;
}

.course-combined-table .course-table th,
.course-combined-table .course-table td {
  border-right: 1px solid #d7e1e8 !important;
  border-bottom: 1px solid #d7e1e8 !important;
}

.course-combined-table .course-table th:last-child,
.course-combined-table .course-table td:last-child {
  border-right: 0 !important;
}

.course-combined-table .course-table tbody tr:last-child td {
  border-bottom: 0 !important;
}


/* v8：English 3D 移除 curriculum-table-scroll 最外層共用框 */
.curriculum-table-scroll.course-mode {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: visible;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.curriculum-table-scroll.course-mode .course-combined-table {
  border: 1px solid #d7e1e8 !important;
  border-radius: 0 !important;
  background: #fff;
}


/* v9：只有融合表格保留圓角，Course 外層維持無框 */
.curriculum-table-scroll.course-mode {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}

.curriculum-table-scroll.course-mode .course-combined-table {
  border: 1px solid #d7e1e8 !important;
  border-radius: 12px !important;
  overflow: hidden !important;
  background: #fff;
}


/* v10：封面名稱＋文本類型內容靠左 */
.course-cover-caption {
  display: block;
  margin-top: 8px;
  color: #40586b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
}

.course-combined-table .course-table tbody td:first-child {
  text-align: left !important;
}

@media (max-width: 767px) {
  .course-cover-caption {
    margin-top: 6px;
    font-size: 11px;
  }
}


</style>
